import { useCallback, useEffect, useState } from "react";
import { RefreshCw, DatabaseBackup, Download, RotateCcw, X, AlertTriangle } from "lucide-react";
import { api, API_BASE, errorToMessage, getStoredAuth } from "@/lib/apiClient";
import { canBackupWrite, useAuth } from "@/lib/auth";
import { BACKUP } from "@/constants/testIds";

const STATUS_COLORS = {
  READY: "bg-emerald-100 text-emerald-800",
  RUNNING: "bg-sky-100 text-sky-800",
  FAILED: "bg-rose-100 text-rose-800",
};

function bytes(n) {
  const v = Number(n || 0);
  if (v < 1024) return `${v} B`;
  if (v < 1024 * 1024) return `${(v / 1024).toFixed(1)} KB`;
  if (v < 1024 * 1024 * 1024) return `${(v / (1024 * 1024)).toFixed(2)} MB`;
  return `${(v / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function Modal({ title, open, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
        <div className="flex items-center justify-between border-b border-slate-100 px-5 py-3">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={16} /></button>
        </div>
        <div className="px-5 py-4">{children}</div>
      </div>
    </div>
  );
}

export default function BackupPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [createOpen, setCreateOpen] = useState(false);
  const [form, setForm] = useState({ include_files: true, remarks: "" });
  const [busy, setBusy] = useState(false);
  const [actionErr, setActionErr] = useState(null);

  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restoreConfirmText, setRestoreConfirmText] = useState("");
  const [restoreResult, setRestoreResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/backups");
      setRows(data);
    } catch (e) {
      setError(errorToMessage(e, "Failed to load backups"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createBackup = async () => {
    setBusy(true);
    setActionErr(null);
    try {
      await api.post("/backups", {
        include_files: form.include_files,
        remarks: form.remarks || null,
      });
      setCreateOpen(false);
      setForm({ include_files: true, remarks: "" });
      await load();
    } catch (e) {
      setActionErr(errorToMessage(e, "Backup creation failed"));
    } finally {
      setBusy(false);
    }
  };

  const download = (id, filename) => {
    const stored = getStoredAuth();
    const token = stored?.access_token;
    (async () => {
      try {
        const resp = await fetch(`${API_BASE}/backups/${id}/download`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
        const blob = await resp.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      } catch (e) {
        setActionErr(errorToMessage(e, "Download failed"));
      }
    })();
  };

  const restore = async () => {
    if (!restoreTarget) return;
    setBusy(true);
    setActionErr(null);
    try {
      const { data } = await api.post(`/backups/${restoreTarget.id}/restore`);
      setRestoreResult(data);
      await load();
    } catch (e) {
      setActionErr(errorToMessage(e, "Restore failed"));
    } finally {
      setBusy(false);
    }
  };

  const canWrite = canBackupWrite(user);

  return (
    <div className="space-y-6" data-testid="backup-page">
      <div className="flex items-baseline justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-900">Backup &amp; Restore</h1>
          <p className="text-sm text-slate-500 mt-1">
            On-demand PostgreSQL dumps + optional file bundle, stored locally as
            ZIP snapshots. SUPER_ADMIN only for create and restore.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button type="button" onClick={load} data-testid={BACKUP.refresh}
            className="inline-flex items-center gap-2 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">
            <RefreshCw size={14} /> Refresh
          </button>
          {canWrite && (
            <button type="button" data-testid={BACKUP.createBtn}
              onClick={() => setCreateOpen(true)}
              className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800">
              <DatabaseBackup size={14} /> New backup
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {error}
        </div>
      )}
      {actionErr && (
        <div className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
          {actionErr}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
        <table className="w-full text-sm" data-testid={BACKUP.table}>
          <thead className="bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="text-left px-4 py-3">Created</th>
              <th className="text-left px-4 py-3">Filename</th>
              <th className="text-right px-4 py-3">Size</th>
              <th className="text-right px-4 py-3">DB</th>
              <th className="text-right px-4 py-3">Files</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">By</th>
              <th className="text-left px-4 py-3">Remarks</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">Loading…</td></tr>}
            {!loading && rows.length === 0 && (
              <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No backups yet.</td></tr>
            )}
            {!loading && rows.map((r) => (
              <tr key={r.id} data-testid={BACKUP.row(r.id)} className="border-t border-slate-100">
                <td className="px-4 py-2 text-xs text-slate-600">{new Date(r.created_at).toLocaleString()}</td>
                <td className="px-4 py-2 font-mono text-xs text-slate-900">{r.filename}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{bytes(r.size_bytes)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{bytes(r.db_bytes)}</td>
                <td className="px-4 py-2 text-right font-mono text-xs">{bytes(r.files_bytes)}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[r.status] || "bg-slate-100 text-slate-700"}`}>
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-slate-600">{r.created_by_email || "—"}</td>
                <td className="px-4 py-2 text-xs text-slate-600 max-w-xs truncate">{r.remarks || "—"}</td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {r.status === "READY" && (
                    <button data-testid={BACKUP.download(r.id)}
                      onClick={() => download(r.id, r.filename)}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 bg-white px-2 py-1 text-xs text-slate-700 hover:bg-slate-50 mr-2">
                      <Download size={12} /> Download
                    </button>
                  )}
                  {canWrite && r.status === "READY" && (
                    <button data-testid={BACKUP.restore(r.id)}
                      onClick={() => { setRestoreTarget(r); setRestoreConfirmText(""); setRestoreResult(null); }}
                      className="inline-flex items-center gap-1 rounded-md border border-amber-300 bg-white px-2 py-1 text-xs text-amber-700 hover:bg-amber-50">
                      <RotateCcw size={12} /> Restore
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Create modal */}
      <Modal title="Create backup" open={createOpen} onClose={() => setCreateOpen(false)}>
        <div className="space-y-3">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" data-testid={BACKUP.includeFiles}
              checked={form.include_files}
              onChange={(e) => setForm({ ...form, include_files: e.target.checked })} />
            Include uploaded files (invoices, PDFs, media)
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-slate-500 mb-1">Remarks</span>
            <input data-testid={BACKUP.remarks}
              value={form.remarks}
              onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              placeholder="Optional context — e.g., before schema change"
              className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
          </label>
          <div className="text-xs text-slate-500">
            The dump runs synchronously; large databases may take up to a minute.
          </div>
          <div className="flex justify-end gap-2">
            <button onClick={() => setCreateOpen(false)}
              className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
            <button data-testid={BACKUP.submit} disabled={busy} onClick={createBackup}
              className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-40">
              {busy ? "Creating…" : "Create backup"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Restore modal */}
      <Modal title="Restore from backup" open={!!restoreTarget}
        onClose={() => { setRestoreTarget(null); setRestoreConfirmText(""); setRestoreResult(null); }}>
        {!restoreResult ? (
          <div className="space-y-3">
            <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
              <div className="flex items-start gap-2">
                <AlertTriangle size={16} className="mt-0.5" />
                <div>
                  <div className="font-semibold">This is destructive.</div>
                  <div className="text-xs mt-1">
                    All current data will be replaced by <span className="font-mono">{restoreTarget?.filename}</span>.
                    A safety snapshot of the current state is captured automatically before the restore begins.
                  </div>
                </div>
              </div>
            </div>
            <label className="block">
              <span className="block text-xs font-medium text-slate-500 mb-1">Type <span className="font-mono">RESTORE</span> to confirm</span>
              <input data-testid={BACKUP.restoreConfirmText}
                value={restoreConfirmText}
                onChange={(e) => setRestoreConfirmText(e.target.value)}
                className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
            </label>
            <div className="flex justify-end gap-2">
              <button onClick={() => setRestoreTarget(null)}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Cancel</button>
              <button data-testid={BACKUP.restoreConfirm}
                disabled={busy || restoreConfirmText !== "RESTORE"}
                onClick={restore}
                className="rounded-md bg-rose-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-40">
                {busy ? "Restoring…" : "Restore now"}
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3 text-sm">
            <div className="rounded-md border border-emerald-200 bg-emerald-50 p-3 text-emerald-900">
              Restore complete. Safety snapshot: <span className="font-mono">{restoreResult.safety_snapshot_filename}</span>
            </div>
            <div className="flex justify-end">
              <button onClick={() => { setRestoreTarget(null); setRestoreResult(null); }}
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50">Close</button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
